import { useState, useEffect, useRef } from "react";


const Y = "#FFD000";
const BK = "#0a0a0a";
const G1 = "#1a1a1a";
const G2 = "#2a2a2a";
const G3 = "#3a3a3a";
const WH = "#f5f4f0";
const MT = "#888";

function MaxPlayer() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.__maxInitialized) {
      if (window.MaxBot) window.MaxBot.state('loading');
      return;
    }
    const s = document.createElement("script");
    s.src = "/max/maxplayer.js";
    s.onload = function() {
      if (ref.current && window.MaxBot) {
        window.__maxInitialized = true;
        window.MaxBot.init({ mount: ref.current, basePath: "/max/", screen: "LIFTSTEIN", idlePlayDelay: 12000 });
        window.MaxBot.state('loading');
      }
    };
    document.body.appendChild(s);
  }, []);
  return <div ref={ref} style={{ width: "100%" }} />;
}

export default function App() {
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showLead, setShowLead] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadReqs, setLeadReqs] = useState("");
  // Set only when the customer clicks a per-item "Inquire" button (see
  // formatText() below) -- {kind:'liftstein'|'supplier', ref:string}. Drives
  // unambiguous per-item lead attribution instead of scanning all of Max's
  // replies for the last-mentioned ref. Null means the generic no-items
  // lead form is showing instead.
  const [inquireItem, setInquireItem] = useState(null);
  // True from the moment ANY reply shows at least one item (Liftstein or
  // supplier) for the rest of the session. Once items are on screen the
  // conversation is over -- the customer either clicks Inquire on one of
  // them, starts a new chat, or goes to WhatsApp. This also closes the door
  // on the old "customer says thanks/bye -> show the blanket form" fallback
  // firing after items were already shown, which read as a non-sequitur
  // (items with their own Inquire buttons, followed out of nowhere by an
  // unrelated generic contact form).
  const [itemsShown, setItemsShown] = useState(false);
  const [lang, setLang] = useState("en");
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : ("sess-" + Date.now() + "-" + Math.random().toString(36).slice(2));
  }

  // ─── Supabase conversation logging (silent, non-blocking, never breaks chat) ───
  var SUPABASE_URL = "https://bvymkwzdrrrigtwyuysp.supabase.co";
  var SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ2eW1rd3pkcnJyaWd0d3l1eXNwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODI4MzQ1MTIsImV4cCI6MjA5ODQxMDUxMn0.u9S_jwBGWdG3vcbhvrrgE5y39NyxZgtiSckMCwSUFqc";

  function detectTonnage(text) {
    var m = (text || "").match(/(\d+(\.\d+)?)\s*-?\s*(ton|tonne|t\b)/i);
    return m ? parseFloat(m[1]) : null;
  }
  function detectFuel(text) {
    var t = (text || "").toLowerCase();
    if (/diesel/.test(t)) return "diesel";
    if (/electric|battery/.test(t)) return "electric";
    if (/\blpg\b|\bgas\b/.test(t)) return "lpg";
    return null;
  }
  function detectCategory(text) {
    var t = (text || "").toLowerCase();
    // Not material handling at all → single out_of_scope bucket
    if (/boom.?lift|aerial.?work|scissor.?lift|cherry.?picker|man.?lift|awp|jlg|crane|hoist|conveyor|agv|wheel.?loader|earth.?mov/.test(t)) return "out_of_scope";
    // Material handling types — in Liftstein portfolio
    if (/reach\s*truck|double.?deep|double.?reach/.test(t)) return "reach_truck";
    if (/stacker|walkie.stacker|pedestrian stacker/.test(t)) return "stacker";
    if (/pallet.?truck|pallet.?jack|electric pallet/.test(t)) return "pallet_truck";
    if (/forklift|counterbalance|counter.balance/.test(t)) return "forklift";
    // Material handling types — outside Liftstein stock but can source
    if (/vna|very narrow aisle|turret truck|swing.reach|articulated forklift|aisle.?master|bendi|flexi/.test(t)) return "vna_truck";
    if (/order.?picker|man.?up|order picking/.test(t)) return "order_picker";
    if (/side.?loader|sideloader/.test(t)) return "side_loader";
    if (/rough.?terrain|telehandler|telescopic handler/.test(t)) return "rough_terrain";
    // Smart inference: if tonnage > 3T mentioned without equipment type → must be forklift
    // (reach trucks max 2.5T, stackers max 2T, pallet trucks max 3T)
    var tonnageMatch = (text || "").match(/(\d+(\.\d+)?)\s*-?\s*(ton|tonne|\bt\b)/i);
    if (tonnageMatch && parseFloat(tonnageMatch[1]) > 3) return "forklift";
    return null;
  }
  function detectIntent(text) {
    var t = (text || "").toLowerCase();
    if (/spare\s*part|parts\b/.test(t)) return "spare_parts";
    if (/service|maintenance|repair|breakdown|servic|fixing|fix\b/.test(t)) return "maintenance";
    if (/rent(?!al\s*pack)|rental|hire/.test(t)) return "rental";
    if (/new\s*(forklift|reach|stacker|pallet|machine|equipment|unit)|buy.*new|new.*buy|purchase.*new|new.*purchase/.test(t)) return "purchase_new";
    if (/used\s*(forklift|reach|stacker|pallet|machine|equipment|unit)|buy.*used|used.*buy|purchase.*used|used.*purchase|second.?hand/.test(t)) return "purchase_used";
    if (/buy|purchase|buying|need\s*a\s*(forklift|reach|stacker|pallet)|looking\s*(for|to\s*buy)/.test(t)) return "purchase";
    return null;
  }
  function logConversation(role, text, extra) {
    try {
      var row = {
        session_id: sessionIdRef.current,
        language: lang,
        role: role,
        message: (text || "").slice(0, 2000),
        category: detectCategory(text),
        tonnage: detectTonnage(text),
        fuel_type: detectFuel(text),
        intent: detectIntent(text),
        in_stock: extra && typeof extra.inStock === "boolean" ? extra.inStock : null,
        converted: false
      };
      fetch(SUPABASE_URL + "/rest/v1/conversations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_KEY,
          "Authorization": "Bearer " + SUPABASE_KEY,
          "Prefer": "return=minimal"
        },
        body: JSON.stringify(row)
      }).catch(function() { /* silent — never break the chat experience */ });
    } catch(e) { /* silent */ }
  }
  const bottomRef = useRef(null);

  useEffect(() => {
    // Don't auto-scroll on the initial greeting — let user see Max's intro from the top.
    // Only scroll when the conversation is underway (more than 1 message).
    if (messages.length > 1 || loading || showLead || itemsShown) {
      bottomRef.current && bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, showLead, itemsShown]);

  // Track the real visible viewport (excludes the on-screen keyboard) and expose it
  // as a CSS variable. iOS resizes window.innerHeight unreliably when the keyboard
  // opens/closes, which is what causes the layout to jump/zoom on focus — reading
  // visualViewport directly gives an accurate, immediate value instead.
  useEffect(() => {
    function setAppHeight() {
      var h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      document.documentElement.style.setProperty("--app-vh", h + "px");
    }
    setAppHeight();
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setAppHeight);
      window.visualViewport.addEventListener("scroll", setAppHeight);
    }
    window.addEventListener("resize", setAppHeight);
    window.addEventListener("orientationchange", setAppHeight);
    return function () {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setAppHeight);
        window.visualViewport.removeEventListener("scroll", setAppHeight);
      }
      window.removeEventListener("resize", setAppHeight);
      window.removeEventListener("orientationchange", setAppHeight);
    };
  }, []);

  // Exposes a global hook so the per-item "Inquire" buttons -- rendered as
  // raw HTML strings inside formatText()'s dangerouslySetInnerHTML output,
  // which can't carry real React onClick handlers -- can reach back into
  // React state. setState functions are stable across re-renders, so this
  // only ever needs to run once.
  useEffect(function() {
    // `label` is a short human-readable tag for whichever item was clicked
    // (e.g. "2021 toyota 62 8fd25 fl0023" or "Ref: SUP-4bc1c2c6") so the
    // scoped form can show "Regarding: ..." -- useful once 2+ items are on
    // screen and the customer might click Inquire on one, then change their
    // mind and click Inquire on the other before submitting. Clicking a
    // different item's Inquire button just overwrites inquireItem (already-
    // typed name/phone/email are preserved, only the target item changes).
    window.__maxInquire = function(kind, ref, label) {
      setInquireItem({ kind: kind, ref: ref, label: label || ref });
      setShowLead(true);
    };
    return function() { delete window.__maxInquire; };
  }, []);

  useEffect(() => { reset(); }, []);

  function reset() {
    setMessages([{ role: "ai", text: "Hey! I am Max, Liftstein equipment specialist here in Dubai.\n\nI help businesses across the region find the right material handling equipment — even if you are not sure what you need yet.\n\n**What kind of operation are you running?**" }]);
    setHistory([]);
    setLang("en");
    setQuickReplies(["Warehouse / Logistics", "Construction Site", "Cold Storage / Food", "Loading Dock / Port", "Manufacturing", "Not sure yet"]);
    setShowLead(false);
    setLeadDone(false);
    setInquireItem(null);
    setItemsShown(false);
    setLeadName("");
    setLeadPhone("");
    setLeadEmail("");
    setLeadReqs("");
    setInput("");
    setLoading(false);
    setTimeout(function() { if (window.MaxBot) window.MaxBot.state('greeting'); }, 400);
  }

  function toggleLang() {
    var next = lang === "en" ? "ar" : lang === "ar" ? "de" : "en";
    setLang(next);
    var greeting = next === "ar"
      ? "مرحباً! أنا ماكس، أخصائي المعدات في ليفتشتاين دبي. أساعد الشركات في المنطقة على إيجاد معدات المناولة المثالية. ما نوع العمليات التي تقوم بها؟"
      : next === "de"
      ? "Hallo! Ich bin Max, Ihr Materialhandling-Spezialist bei Liftstein Dubai. Ich helfe Unternehmen in der Region, die richtige Ausrüstung zu finden. Was für einen Betrieb führen Sie?"
      : "Hey! I am Max, Liftstein equipment specialist here in Dubai. I help businesses across the region find the right material handling equipment. What kind of operation are you running?";
    setMessages([{ role: "ai", text: greeting }]);
    setHistory([]);
    var qr = next === "ar"
      ? ["مستودع / لوجستيات", "موقع بناء", "تخزين بارد / غذاء", "رصيف تحميل / ميناء", "تصنيع", "لست متأكداً"]
      : next === "de"
      ? ["Lager / Logistik", "Baustelle", "Kühlhaus / Lebensmittel", "Laderampe / Hafen", "Fertigung", "Nicht sicher"]
      : ["Warehouse / Logistics", "Construction Site", "Cold Storage / Food", "Loading Dock / Port", "Manufacturing", "Not sure yet"];
    setQuickReplies(qr);
    setShowLead(false);
    setLeadDone(false);
    setInquireItem(null);
    setItemsShown(false);
    setLeadName("");
    setLeadPhone("");
    setLeadEmail("");
    setLeadReqs("");
  }

  async function send(text) {
    var msg = (text || input).trim();
    // Once the lead form has appeared, OR any items have been shown, the
    // conversation is over -- the customer must click Inquire on an item,
    // fill the generic form, start a new chat, or go to WhatsApp.
    // Belt-and-braces guard alongside the input area being hidden below.
    if (!msg || loading || showLead || itemsShown) return;
    setQuickReplies([]);
    setInput("");
    setMessages(function(prev) { return prev.concat([{ role: "user", text: msg }]); });
    var newHistory = history.concat([{ role: "user", content: msg }]);
    setHistory(newHistory);
    setLoading(true);
    logConversation("user", msg);
    if (window.MaxBot) window.MaxBot.state('thinking');

    try {
      var response = await fetch("/.netlify/functions/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5",
          max_tokens: 1536,
          lang: lang,
          messages: newHistory.slice(-20)
        })
      });
      var data = await response.json();
      var reply = "", chest = "", showFormFlag = false, gotToolReply = false, genericFailure = false;

      // Primary path: the backend now forces every reply through Anthropic's
      // tool-use API (see anthropic-proxy.js), which constrains the model's
      // output to valid {reply, chest, show_form} at the decoding level --
      // this is what actually guarantees a well-formed reply, unlike the old
      // approach of asking the model to hand-format raw JSON text (which
      // could occasionally drift on off-topic/novel inputs).
      if (data && Array.isArray(data.content)) {
        var toolBlock = null;
        for (var ti = 0; ti < data.content.length; ti++) {
          if (data.content[ti].type === "tool_use" && data.content[ti].name === "send_reply") { toolBlock = data.content[ti]; break; }
        }
        if (toolBlock && toolBlock.input && toolBlock.input.reply) {
          reply = toolBlock.input.reply;
          chest = toolBlock.input.chest || "";
          showFormFlag = toolBlock.input.show_form === true;
          gotToolReply = true;
        }
      }

      if (!gotToolReply) {
        // Fallback path -- only reached if the API call itself errored or
        // returned something unexpected (rate limit, outage, etc.), not
        // during normal operation now that tool-use is forced.
        var raw = "";
        if (data && data.content) {
          for (var i = 0; i < data.content.length; i++) {
            if (data.content[i].type === "text") raw += data.content[i].text;
          }
        }
        if (!raw) raw = '{"reply":"Sorry, I did not get a response. Please try again.","chest":""}';
        reply = raw;
        try {
          var cleaned = raw.replace(/```json/gi, '').replace(/```/g, '').trim();
          var jsonMatch = cleaned.match(/\{[\s\S]*\}/);
          var jsonStr = jsonMatch ? jsonMatch[0] : cleaned;
          var parsed = JSON.parse(jsonStr);
          reply = parsed.reply || raw;
          chest = parsed.chest || "";
          showFormFlag = parsed.show_form === true;
        } catch(e) {
          var replyFieldMatch = raw.match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)/);
          if (replyFieldMatch && replyFieldMatch[1]) {
            try {
              reply = JSON.parse('"' + replyFieldMatch[1] + '"');
            } catch (e2) {
              reply = replyFieldMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
            }
          } else {
            reply = "Sorry, something went wrong on my end — could you say that again?";
            genericFailure = true;
          }
        }
      }

      if (window.MaxBot) window.MaxBot.state('talking', chest);
      setMessages(function(prev) { return prev.concat([{ role: "ai", text: reply }]); });
      // Don't feed the generic apology back into Max's own context as if he'd
      // said it -- it isn't a real answer, and leaving it out of `history`
      // (while still showing it in the visible chat) keeps a rare transient
      // hiccup from coloring how the model responds to the customer's next
      // message.
      if (!genericFailure) {
        setHistory(function(prev) { return prev.concat([{ role: "assistant", content: reply }]); });
      }
      logConversation("assistant", reply, { inStock: reply.includes('liftstein.com/products/') });

      // Does this reply show at least one item -- a Liftstein stock link or a
      // supplier-network Ref code? If so, every item gets its own "View
      // Product" + "Inquire" button (see formatText()) and attribution
      // happens per-item when the customer clicks Inquire on a specific one
      // -- never through a blanket end-of-conversation form, per the
      // redesign: each supplier (and Liftstein) should know unambiguously
      // whether a customer was interested in THEIR item, not a guess based
      // on whatever ref was last mentioned.
      var hasItems = /liftstein\.com\/products\//.test(reply) || /\(?\s*Ref:?\s*SUP-[a-f0-9]{8}\s*\)?/i.test(reply);
      if (hasItems) { setItemsShown(true); }

      if (!leadDone) {
        if (hasItems) {
          // Never auto-show the generic contact-detail form when items are
          // on screen -- but DO close the conversation right here (see
          // itemsShown above / the input-area JSX below): once items are
          // shown, that reply is the endpoint. The customer clicks Inquire
          // on whichever item they want, or exits via Start New Chat /
          // WhatsApp -- there's no more free-text turn where a stray
          // "thanks" or "ok" could wander into the old goodbye-without-form
          // fallback and pop up an unrelated generic contact form.
          setShowLead(false);
        } else {
          // showFormFlag (parsed from Max's JSON) is the primary, language-agnostic
          // signal and covers Arabic/German replies exactly the same as English.
          // The text-based checks below are only a fallback for the rare case where
          // Max's JSON fails to parse — include EN/AR/DE phrasing so that fallback
          // isn't accidentally English-only.
          var replyLower = reply.toLowerCase();
          var shouldShowForm = !!(
            showFormFlag ||
            replyLower.includes("what's your name") ||
            replyLower.includes("your name") ||
            replyLower.includes("whatsapp number") ||
            replyLower.includes("phone number") ||
            replyLower.includes("leave your") ||
            replyLower.includes("fill in the form") ||
            replyLower.includes("fill the form") ||
            // Arabic fallback phrasing (leave your details / fill the form / WhatsApp / phone number)
            reply.includes("اترك") || reply.includes("بياناتك") ||
            reply.includes("املأ النموذج") || reply.includes("النموذج") ||
            reply.includes("رقم الواتساب") || reply.includes("رقم الهاتف") ||
            // German fallback phrasing (leave your details / fill the form / WhatsApp / phone number)
            replyLower.includes("hinterlassen sie") || replyLower.includes("ihre daten") ||
            replyLower.includes("füllen sie das formular") || replyLower.includes("formular aus") ||
            replyLower.includes("telefonnummer") || replyLower.includes("whatsapp-nummer")
          );
          // Sync to whatever THIS reply indicates — including hiding the form
          // again if the conversation legitimately resumed qualifying questions
          // (e.g. customer corrects Max: "actually it's for Dubai") and this
          // reply's show_form came back false. Previously this only ever
          // turned the form ON and could never turn it back off.
          setShowLead(shouldShowForm);
        }
      }
      // Play the celebration animation any time a Liftstein stock match is
      // shown, independent of the (now item-scoped, not blanket-form) UI.
      if (reply.includes('liftstein.com/products/') && window.MaxBot) {
        window.MaxBot.state('celebrate', 'MATCH');
      }
      // No explicit idle call needed — talking plays once then auto-returns to idle in v3
    } catch(err) {
      if (window.MaxBot) window.MaxBot.state('idle');
      setMessages(function(prev) { return prev.concat([{ role: "ai", text: "Error: " + err.message }]); });
    }
    setLoading(false);
  }

  function submitLead() {
    if (leadSubmitting || leadDone) return; // guard against double-click / double-tap firing two lead emails
    if (!leadName || !leadPhone) { alert("Please enter name and phone."); return; }
    setLeadSubmitting(true);

    var summary = history
      .filter(function(m) { return m.role === "user"; })
      .map(function(m) { return m.content; })
      .join(" | ");

    // Log conversion event as new INSERT row (more reliable than PATCH, uses existing INSERT policy)
    try {
      fetch(SUPABASE_URL + "/rest/v1/conversations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_KEY,
          "Authorization": "Bearer " + SUPABASE_KEY,
          "Prefer": "return=minimal"
        },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          language: lang,
          role: "conversion",
          message: "lead_form_submitted",
          converted: true
        })
      }).catch(function() {});
    } catch(e) {}

    // Attribution: if the customer clicked "Inquire" on a specific item,
    // inquireItem already tells us exactly which one -- use that directly,
    // no guessing. Only fall back to scanning Max's replies for the last-
    // mentioned supplier ref when this is the generic no-items lead form
    // (inquireItem is null), which is the one case where there's no single
    // item to point to.
    var supplierRef = null;
    var shopifyHandle = null;
    if (inquireItem && inquireItem.kind === "supplier") {
      supplierRef = inquireItem.ref;
    } else if (inquireItem && inquireItem.kind === "liftstein") {
      shopifyHandle = inquireItem.ref;
    } else {
      var allAiText = messages.filter(function(m) { return m.role === "ai"; }).map(function(m) { return m.text; }).join(" ");
      var refMatches = allAiText.match(/SUP-[a-f0-9]{8}/gi);
      supplierRef = refMatches && refMatches.length ? refMatches[refMatches.length - 1] : null;
    }

    function finishLead() {
      // If running standalone (not embedded in an iframe), redirect to thank-you page
      if (window.self === window.top) {
        window.location.href = "https://liftstein.com/pages/thank-you";
      } else {
        // Embedded as iframe — just show success state
        setShowLead(false);
        setLeadDone(true);
        setMessages(function(prev) { return prev.concat([{ role: "ai", text: "Thanks " + leadName + "! Your details have been sent to the team. Expect a WhatsApp message within a few hours." }]); });
      }
    }

    // This single call both logs the lead in Supabase AND sends the "New
    // Lead from Max" email notification (handled server-side inside
    // create-lead.js) -- keeping the Make.com webhook call out of the
    // browser entirely, so its URL is never exposed in the public JS bundle.
    fetch("/.netlify/functions/create-lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        name: leadName,
        phone: leadPhone,
        email: leadEmail || null,
        language: lang,
        requirements: leadReqs || summary || null,
        supplier_ref: supplierRef,
        shopify_handle: shopifyHandle
      })
    }).then(finishLead).catch(function(err) {
      console.error("Lead submit error:", err);
      finishLead();
    });
  }

  function getEquipmentIcon(context) {
    var ctx = (context || '').toLowerCase();
    var reachTruckSVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="12" width="8" height="10" rx="1"/><line x1="12" y1="2" x2="12" y2="12"/><line x1="9" y1="6" x2="15" y2="6"/><line x1="6" y1="18" x2="8" y2="18"/><line x1="16" y1="18" x2="18" y2="18"/><rect x="9" y="3" width="6" height="3" rx="0.5"/></svg>';
    var forkliftSVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="10" width="10" height="8" rx="1"/><line x1="2" y1="14" x2="8" y2="14"/><line x1="2" y1="17" x2="8" y2="17"/><line x1="18" y1="18" x2="18" y2="22"/><circle cx="10" cy="21" r="2"/><circle cx="19" cy="21" r="2"/><line x1="12" y1="10" x2="12" y2="6"/><line x1="15" y1="10" x2="15" y2="8"/></svg>';
    var palletSVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="8" width="14" height="6" rx="1"/><line x1="2" y1="11" x2="16" y2="11"/><line x1="5" y1="14" x2="5" y2="18"/><line x1="11" y1="14" x2="11" y2="18"/><circle cx="5" cy="20" r="2"/><circle cx="11" cy="20" r="2"/><line x1="16" y1="10" x2="22" y2="10"/></svg>';
    var stackerSVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="14" rx="1"/><line x1="6" y1="12" x2="9" y2="12"/><line x1="6" y1="15" x2="9" y2="15"/><rect x="3" y="12" width="6" height="3"/><circle cx="7" cy="20" r="2"/><circle cx="15" cy="20" r="2"/><line x1="7" y1="18" x2="15" y2="18"/></svg>';
    if (ctx.includes('reach truck') || ctx.includes('reach-truck') || ctx.includes('rre') || ctx.includes('rrn')) return reachTruckSVG;
    if (ctx.includes('pallet truck') || ctx.includes('pallet-truck') || ctx.includes('lhe') || ctx.includes('lpe') || ctx.includes('order picker')) return palletSVG;
    if (ctx.includes('stacker') || ctx.includes('spe') || ctx.includes('swe')) return stackerSVG;
    if (ctx.includes('forklift') || ctx.includes('toyota') || ctx.includes('doosan') || ctx.includes('jungheinrich') || ctx.includes('fl0') || ctx.includes('8fb') || ctx.includes('8fd')) return forkliftSVG;
    return forkliftSVG;
  }

  function formatText(text) {
    // formatText() closes over `lang` (component state) so these button
    // labels track whatever language the conversation is currently in --
    // an Arabic or German customer should never see a stray English word
    // baked into Max's own UI chrome.
    var viewProductLabel = lang === "ar" ? "عرض المنتج" : lang === "de" ? "Produkt ansehen" : "View Product";
    var inquireLabel = lang === "ar" ? "استفسار" : lang === "de" ? "Anfragen" : "Inquire";
    var result = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    // Every recommended item -- Liftstein stock or supplier-sourced -- gets
    // a consistent "View Product" link PLUS its own "Inquire" button. The
    // Inquire button can't be a real React onClick (this whole block is raw
    // HTML injected via dangerouslySetInnerHTML), so it calls a global hook
    // -- window.__maxInquire(kind, ref) -- wired up via useEffect above,
    // which sets inquireItem + opens the scoped lead-capture form. This is
    // what makes per-item lead attribution unambiguous: each supplier (and
    // Liftstein) knows definitively whether a customer was interested in
    // THEIR specific item, instead of guessing from whichever ref Max
    // happened to mention last.
    result = result.replace(/(https?:\/\/liftstein\.com\/products\/[^\s<[]+)(\s*\[category:([^\]]+)\])?/g, function(match, url, catBlock, category) {
      var context = (category || '') + ' ' + (url.split('/products/')[1] || '');
      var icon = getEquipmentIcon(context);
      var handle = (url.split('/products/')[1] || '').split(/[?#]/)[0].replace(/'/g, '');
      // If the customer already clicked Inquire on THIS item, show it as
      // selected (filled, with a checkmark) so it's obvious which item the
      // open form below refers to -- important once 2+ items are on screen,
      // since clicking a different item's Inquire button just re-targets
      // the same form (see window.__maxInquire above).
      var isSelected = showLead && inquireItem && inquireItem.kind === 'liftstein' && inquireItem.ref === handle;
      var btnStyle = isSelected
        ? "display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:#FFD000;color:#0a0a0a;border:1px solid #FFD000;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;"
        : "display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:transparent;color:#FFD000;border:1px solid #FFD000;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;";
      var readableLabel = handle.replace(/-/g, ' ').replace(/'/g, "\\'");
      return '<span style="display:inline-flex;flex-wrap:wrap;gap:8px;margin:6px 0;">'
        + '<a href="' + url + '" target="_blank" rel="noreferrer" style="display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:#FFD000;color:#0a0a0a;border-radius:8px;text-decoration:none;font-size:13px;font-weight:700;">' + icon + ' ' + viewProductLabel + '</a>'
        + '<button type="button" onclick="window.__maxInquire &amp;&amp; window.__maxInquire(\'liftstein\',\'' + handle + '\',\'' + readableLabel + '\')" style="' + btnStyle + '">' + (isSelected ? '✓ ' : '') + inquireLabel + '</button>'
        + '</span>';
    });
    result = result.replace(/(https?:\/\/(?!liftstein\.com\/products)[^\s<]+)/g, function(url) {
      return '<a href="' + url + '" target="_blank" rel="noreferrer" style="color:#FFD000;text-decoration:underline;">' + url + '</a>';
    });
    // Supplier-sourced units don't have a liftstein.com product page -- Max
    // is instructed to plainly write "(Ref: SUP-xxxxxxxx)" for these instead,
    // but the raw inventory data it reads from actually formats it WITHOUT
    // parens ("Ref: SUP-xxxxxxxx" -- see the supplier line builder in
    // anthropic-proxy.js). The prompt's "(Ref: ...)" is only an example, so
    // the model sometimes drops the parens when it restructures a sentence
    // (especially translating into Arabic/German), which used to leave the
    // raw ref code on screen with no button. Match both forms -- parens
    // optional -- so the button always renders regardless of how Max
    // formatted it, and any parens present get consumed by the match so none
    // are left dangling in the output.
    var cameraIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';
    result = result.replace(/\(\s*Ref:?\s*(SUP-[a-f0-9]{8})\s*\)|Ref:?\s*(SUP-[a-f0-9]{8})/gi, function(match, refInParens, refBare) {
      var ref = refInParens || refBare;
      var isSelected = showLead && inquireItem && inquireItem.kind === 'supplier' && inquireItem.ref === ref;
      var btnStyle = isSelected
        ? "display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:#FFD000;color:#0a0a0a;border:1px solid #FFD000;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;"
        : "display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:transparent;color:#FFD000;border:1px solid #FFD000;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;";
      var readableLabel = "Ref: " + ref;
      return '<span style="display:inline-flex;flex-wrap:wrap;gap:8px;margin:6px 0;">'
        + '<a href="/item.html?ref=' + ref + '" target="_blank" rel="noreferrer" style="display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:#FFD000;color:#0a0a0a;border-radius:8px;text-decoration:none;font-size:13px;font-weight:700;">' + cameraIcon + ' ' + viewProductLabel + '</a>'
        + '<button type="button" onclick="window.__maxInquire &amp;&amp; window.__maxInquire(\'supplier\',\'' + ref + '\',\'' + readableLabel + '\')" style="' + btnStyle + '">' + (isSelected ? '✓ ' : '') + inquireLabel + '</button>'
        + '</span>';
    });
    result = result.replace(/\n/g, "<br/>");
    return result;
  }

  var inputStyle = { background: "#141414", border: "1px solid #242424", borderRadius: 0, padding: "11px 14px", color: "#D8D6CF", fontSize: 16, fontFamily: "inherit", outline: "none", width: "100%" };

  return (
    <div className="ls-root">
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lsPulse { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes lsGlow { 0%,100%{box-shadow:0 0 6px rgba(234,176,0,0.3)} 50%{box-shadow:0 0 12px rgba(234,176,0,0.6)} }

        .fade { animation: fadeUp 0.28s cubic-bezier(0.32,0.72,0,1); }

        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #EAB000; border-radius: 0; }

        /* ─── ROOT ─── */
        .ls-root {
          background: #080808;
          color: #E0DDD6;
          height: 100dvh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          font-family: 'Outfit', system-ui, sans-serif;
          position: relative;
        }

        /* Dot-grid tactical background */
        .ls-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.032) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
          z-index: 0;
        }
        /* Mechanical noise / film grain — adds physical depth to flat black */
        .ls-root::after {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          opacity: 0.05;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 0;
        }
        .ls-root > * { position: relative; z-index: 1; }

        /* ─── HEADER ─── */
        .ls-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px 32px;
          background: rgba(8,8,8,0.97);
          border-bottom: 1px solid #151515;
          position: sticky;
          top: 0;
          z-index: 100;
          flex-shrink: 0;
        }
        /* Gold gradient rule below header */
        .ls-header::after {
          content: '';
          position: absolute;
          bottom: -1px; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent 0%, #EAB000 20%, #EAB000 80%, transparent 100%);
          opacity: 0.8;
        }

        .ls-logo { height: 108px; width: auto; display: block; }
        .ls-header-right { display: flex; align-items: center; gap: 10px; }

        .ls-reset-btn {
          background: transparent;
          border: 1px solid #222;
          color: #8a8a8a;
          padding: 5px 14px;
          border-radius: 0;
          font-size: 9px;
          cursor: pointer;
          letter-spacing: 2.5px;
          font-family: 'Space Mono', monospace;
          text-transform: uppercase;
          transition: border-color 0.18s, color 0.18s, background 0.18s;
        }
        .ls-reset-btn:hover {
          border-color: #EAB000;
          color: #EAB000;
          background: rgba(234,176,0,0.05);
        }
        .ls-reset-btn:active { transform: translateY(1px); }

        /* ─── BODY LAYOUT ─── */
        .ls-body {
          display: flex;
          flex-direction: row;
          align-items: stretch;
          flex: 1;
          max-width: 1480px;
          margin: 0 auto;
          width: 100%;
          min-height: 0;
          overflow: hidden;
          border-left: 1px solid #111;
          border-right: 1px solid #111;
        }

        /* ─── MAX PANEL ─── */
        .ls-max-panel {
          width: 530px;
          flex-shrink: 0;
          padding: 32px 26px 26px;
          position: sticky;
          top: 0;
          height: 100dvh;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          border-right: 1px solid #131313;
          overflow: hidden;
        }

        /* New: logo column (hidden on desktop, shown on mobile) */
        .ls-panel-logo-col { display: none; }
        /* New: info column (hidden on desktop, shown on mobile) */
        .ls-panel-info-col { display: none; }
        /* New: MAX/ACTIVE status bar below robot row (hidden on desktop) */
        .ls-max-status-bar { display: none; }
        /* New: panel logo (smaller than header logo) */
        .ls-panel-logo { width: 100%; height: auto; display: block; }
        /* Ambient gold glow behind MAX — adds atmospheric depth */
        .ls-max-panel::before {
          content: '';
          position: absolute;
          top: 40px;
          left: 50%;
          transform: translateX(-50%);
          width: 580px;
          height: 580px;
          background: radial-gradient(circle, rgba(234,176,0,0.13) 0%, rgba(234,176,0,0.04) 45%, transparent 72%);
          pointer-events: none;
          z-index: 0;
        }
        .ls-max-panel-top {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        /* ─── MAX BOX — command monitor ─── */
        .ls-max-box {
          width: 478px;
          background: #060606;
          border: 2px solid #EAB000;
          position: relative;
          flex-shrink: 0;
          /* Double-bezel outer shell */
          box-shadow:
            0 0 0 5px #080808,
            0 0 0 6px #151515,
            0 32px 80px rgba(234,176,0,0.10),
            0 6px 20px rgba(0,0,0,0.85);
        }

        /* Top-left corner bracket */
        .ls-max-box::before {
          content: '';
          position: absolute;
          top: -9px; left: -9px;
          width: 26px; height: 26px;
          border-top: 3px solid #FFD000;
          border-left: 3px solid #FFD000;
          pointer-events: none;
          z-index: 4;
        }
        /* Bottom-right corner bracket */
        .ls-max-box::after {
          content: '';
          position: absolute;
          bottom: -9px; right: -9px;
          width: 26px; height: 26px;
          border-bottom: 3px solid #FFD000;
          border-right: 3px solid #FFD000;
          pointer-events: none;
          z-index: 4;
        }

        /* Top-right + bottom-left corners via .ls-max-screen sibling */
        .ls-corner-tr {
          position: absolute;
          top: -9px; right: -9px;
          width: 26px; height: 26px;
          border-top: 3px solid #FFD000;
          border-right: 3px solid #FFD000;
          pointer-events: none;
          z-index: 4;
        }
        .ls-corner-bl {
          position: absolute;
          bottom: -9px; left: -9px;
          width: 26px; height: 26px;
          border-bottom: 3px solid #FFD000;
          border-left: 3px solid #FFD000;
          pointer-events: none;
          z-index: 4;
        }

        /* MAX monitor top bar */
        .ls-max-box-hd {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 9px 14px;
          background: #040404;
          border-bottom: 1px solid rgba(234,176,0,0.55);
        }
        .ls-max-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 6px;
          color: #EAB000;
          font-family: 'Space Mono', monospace;
          text-transform: uppercase;
        }
        .ls-max-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 8px;
          color: #4ade80;
          font-family: 'Space Mono', monospace;
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        .ls-status-dot {
          width: 6px; height: 6px;
          background: #4ade80;
          border-radius: 50%;
          flex-shrink: 0;
          animation: lsPulse 2.4s ease-in-out infinite;
        }

        /* Video screen area */
        .ls-max-screen {
          background: #030303;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          overflow: hidden;
          position: relative;
        }
        /* CRT scanline overlay — stronger */
        .ls-max-screen::after {
          content: '';
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            transparent 0px,
            transparent 2px,
            rgba(0,0,0,0.11) 2px,
            rgba(0,0,0,0.11) 3px
          );
          pointer-events: none;
          z-index: 2;
        }

        /* Mobile-only frame brackets for the video — hidden at desktop, where
           the outer-box brackets already wrap the whole monitor. */
        .ls-screen-corner { display: none; }

        /* Monitor bottom bar */
        .ls-max-box-ft {
          padding: 9px 14px;
          border-top: 1px solid rgba(234,176,0,0.18);
          background: #040404;
        }
        .ls-max-ft-title {
          display: block;
          font-size: 8px;
          color: #9c9990;
          font-family: 'Space Mono', monospace;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 3px;
        }
        .ls-max-ft-loc {
          display: block;
          font-size: 8px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 0.8px;
        }

        /* Caption below monitor */
        .ls-max-caption {
          font-size: 8px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 3px;
          text-transform: uppercase;
          text-align: center;
        }

        /* Trust data grid below MAX */
        .ls-trust-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: #131313;
          border: 1px solid #131313;
          width: 478px;
          flex-shrink: 0;
        }
        .ls-trust-item {
          background: #080808;
          padding: 11px 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .ls-trust-val {
          font-size: 14px;
          font-weight: 700;
          color: #EAB000;
          font-family: 'Space Mono', monospace;
          letter-spacing: 0.5px;
        }
        .ls-trust-key {
          font-size: 7px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        /* Direct-line contact block — anchors bottom of MAX panel */
        .ls-direct-line {
          width: 478px;
          flex-shrink: 0;
          border: 1px solid #131313;
          background: #080808;
          position: relative;
          z-index: 1;
        }
        .ls-direct-line-hd {
          padding: 9px 14px 7px;
          font-size: 8px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 2px;
          text-transform: uppercase;
          border-bottom: 1px solid #131313;
        }
        .ls-direct-line-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          text-decoration: none;
          color: inherit;
          transition: background 0.15s;
        }
        .ls-direct-line-link:hover { background: rgba(37,211,102,0.06); }
        .ls-direct-line-icon {
          width: 34px; height: 34px;
          background: #25D366;
          border-radius: 2px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .ls-direct-line-text { display: flex; flex-direction: column; gap: 3px; }
        .ls-direct-line-label {
          font-size: 8px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }
        .ls-direct-line-num {
          font-size: 14px;
          color: #D8D6CF;
          font-family: 'Space Mono', monospace;
          letter-spacing: 0.5px;
        }

        /* ─── CHAT COLUMN ─── */
        .ls-chat-col {
          flex: 1;
          min-width: 0;
          min-height: 0;
          padding: 34px 34px 24px 30px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          overflow: hidden;
        }

        /* ─── HERO BLOCK ─── */
        .ls-hero {
          flex-shrink: 0;
        }
        .ls-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 9px;
          color: #EAB000;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-family: 'Space Mono', monospace;
          margin-bottom: 16px;
          border: 1px solid rgba(234,176,0,0.25);
          padding: 5px 13px;
          opacity: 0.95;
        }
        .ls-hero-badge::before {
          content: '//';
          font-family: 'Space Mono', monospace;
          color: rgba(234,176,0,0.5);
          font-size: 9px;
        }
        .ls-hero-h1 {
          font-size: clamp(28px, 4.4vw, 52px);
          line-height: 1.01;
          font-weight: 800;
          margin-bottom: 16px;
          letter-spacing: -0.035em;
          font-family: 'Space Grotesk', system-ui, sans-serif;
          text-wrap: balance;
          color: #ECEAE2;
        }
        .ls-hero-p {
          font-size: 15px;
          color: #b5b2a8;
          line-height: 1.72;
          max-width: 460px;
          font-weight: 400;
          font-family: 'Space Grotesk', sans-serif;
          padding-bottom: 18px;
          border-bottom: 1px solid #141414;
        }

        /* ─── CHAT WINDOW ─── */
        .ls-chat-win {
          background: #0c0c0c;
          border: 1px solid #1c1c1c;
          border-top: 2px solid #EAB000;
          border-radius: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          position: relative;
        }
        /* Corner ticks tying chat window to MAX monitor's visual system */
        .ls-chat-win::before {
          content: '';
          position: absolute;
          top: -1px; left: -1px;
          width: 16px; height: 16px;
          border-top: 2px solid #EAB000;
          border-left: 2px solid #EAB000;
          pointer-events: none;
          z-index: 5;
        }
        .ls-chat-win::after {
          content: '';
          position: absolute;
          top: -1px; right: -1px;
          width: 16px; height: 16px;
          border-top: 2px solid #EAB000;
          border-right: 2px solid #EAB000;
          pointer-events: none;
          z-index: 5;
        }

        /* Chat window header */
        .ls-cwin-hd {
          background: #0e0e0e;
          padding: 11px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid #191919;
          flex-shrink: 0;
        }
        .ls-cwin-avatar {
          width: 32px; height: 32px;
          border-radius: 0;
          background: #EAB000;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700; color: #060606;
          flex-shrink: 0;
          font-family: 'Space Mono', monospace;
          letter-spacing: 1px;
        }
        .ls-cwin-name {
          font-size: 13px;
          font-weight: 600;
          color: #D4D2CB;
          font-family: 'Space Grotesk', sans-serif;
          letter-spacing: 0.1px;
        }
        .ls-cwin-online {
          display: flex; align-items: center; gap: 5px;
          font-size: 8px; color: #4ade80;
          font-family: 'Space Mono', monospace;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-top: 3px;
        }
        .ls-online-dot {
          width: 5px; height: 5px;
          background: #4ade80;
          border-radius: 50%;
          flex-shrink: 0;
          animation: lsPulse 2.4s ease-in-out infinite;
        }
        .ls-lang-btn {
          background: rgba(234,176,0,0.06);
          border: 1px solid rgba(234,176,0,0.2);
          color: #EAB000;
          padding: 4px 10px;
          border-radius: 0;
          font-size: 9px;
          cursor: pointer;
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .ls-lang-btn:hover { background: rgba(234,176,0,0.13); }
        .ls-lang-btn:active { transform: translateY(1px); }

        /* ─── FOOTER ─── */
        .ls-footer {
          text-align: center;
          font-size: 9px;
          color: #8a8a8a;
          padding: 12px;
          font-family: 'Space Mono', monospace;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          flex-shrink: 0;
          border-top: 1px solid #111;
        }
        .ls-footer a { color: #EAB000; text-decoration: none; opacity: 0.7; }
        .ls-footer a:hover { opacity: 1; }

        /* Fixed corner telemetry readouts — finishes the blueprint feel */
        .ls-corner-readout {
          position: fixed;
          bottom: 14px;
          font-size: 8px;
          color: #8a8a8a;
          font-family: 'Space Mono', monospace;
          letter-spacing: 2px;
          text-transform: uppercase;
          pointer-events: none;
          z-index: 2;
        }
        .ls-corner-readout.left { left: 16px; }
        .ls-corner-readout.right { right: 16px; }

        /* ─── QUICK REPLY HOVER ─── */
        .ls-qr-btn:hover {
          border-color: #EAB000 !important;
          color: #EAB000 !important;
          background: rgba(234,176,0,0.06) !important;
        }

        /* ═══════════════════════════
           RESPONSIVE BREAKPOINTS
        ═══════════════════════════ */

        /* Tablet: ≤900px */
        @media (max-width: 900px) {
          /* Lock the whole app to one screen — only the chat messages scroll.
             This is what keeps MAX permanently visible instead of scrolling away.
             Uses --app-vh (set via JS from visualViewport) instead of raw dvh —
             dvh recalculates unreliably when the iOS keyboard opens/closes, which
             is what causes the layout to visibly jump. */
          .ls-root { height: var(--app-vh, 100dvh); overflow: hidden; }
          .ls-body {
            flex-direction: column;
            max-width: 700px;
            height: calc(var(--app-vh, 100dvh) - 154px);
            overflow: hidden;
          }
          .ls-max-panel {
            width: 100%;
            flex-shrink: 0;
            position: static;
            height: auto;
            padding: 12px 14px;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            border-right: none;
            border-bottom: 1px solid #131313;
            gap: 0;
          }
          .ls-max-panel::before { display: none; }
          .ls-max-panel-top { flex-direction: row; gap: 0; width: 100%; }

          /* MAX box: a row with two equal-width side columns (flex:1 each, so
             they're always perfectly symmetric) flanking the framed video.
             Row height drives the video's size via aspect-ratio — MAX fills
             this height completely, no leftover gap above or below him. */
          .ls-max-box {
            display: flex;
            flex-direction: row;
            align-items: stretch;
            width: 100%;
            height: 300px;
            background: transparent;
            border: none;
            box-shadow: none;
          }
          /* Outer-box brackets are a desktop-only treatment — hide all four. */
          .ls-max-box::before, .ls-max-box::after,
          .ls-corner-tr, .ls-corner-bl { display: none; }

          .ls-max-box-hd, .ls-max-box-ft {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            background: transparent;
            border: none;
            padding: 6px;
            min-width: 0;
          }
          .ls-max-label { font-size: 10px; letter-spacing: 2.5px; }
          .ls-max-status { font-size: 8px; flex-direction: row; justify-content: center; }
          .ls-max-ft-title { font-size: 8px; margin-bottom: 5px; letter-spacing: 1.5px; }
          .ls-max-ft-loc { font-size: 8px; line-height: 1.45; }

          /* MAX himself — height drives width via the player's own
             aspect-ratio:640/854, so he fills the row's full height exactly,
             and the yellow frame hugs only this element, tightly. */
          .ls-max-screen {
            position: relative;
            height: 100%;
            width: auto;
            aspect-ratio: 640 / 854;
            flex-shrink: 0;
            border: 2px solid #EAB000;
            box-shadow: 0 0 0 3px #080808, 0 0 0 4px #151515;
            max-height: none;
          }
          .ls-screen-corner { display: block; position: absolute; width: 16px; height: 16px; pointer-events: none; z-index: 4; }
          .ls-screen-corner-tl { top: -7px; left: -7px; border-top: 3px solid #FFD000; border-left: 3px solid #FFD000; }
          .ls-screen-corner-tr { top: -7px; right: -7px; border-top: 3px solid #FFD000; border-right: 3px solid #FFD000; }
          .ls-screen-corner-bl { bottom: -7px; left: -7px; border-bottom: 3px solid #FFD000; border-left: 3px solid #FFD000; }
          .ls-screen-corner-br { bottom: -7px; right: -7px; border-bottom: 3px solid #FFD000; border-right: 3px solid #FFD000; }

          .ls-max-caption { display: none; }
          .ls-trust-grid { display: none; }
          .ls-direct-line { display: none; }
          /* Hero copy is dropped on small screens — the chat header already
             carries MAX's identity, and the saved height goes to the chat itself. */
          .ls-hero { display: none; }
          .ls-chat-col {
            flex: 1;
            min-height: 0;
            padding: 10px 14px 10px;
            gap: 0;
          }
          .ls-chat-win { flex: 1; min-height: 0; }
          .ls-lang-btn { padding: 7px 16px; font-size: 11px; letter-spacing: 2px; }
        }

        /* Mobile: ≤600px */
        @media (max-width: 600px) {
          .ls-root { height: 100dvh; overflow: hidden; width: 100%; max-width: 100%; box-sizing: border-box; }
          .ls-body { flex: 1; min-height: 0; overflow: hidden; width: 100%; max-width: 100%; box-sizing: border-box; flex-direction: column; }
          .ls-chat-col { overflow: hidden; min-height: 0; width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px 6px; }
          .ls-chat-win { min-height: 0; }

          /* Max panel: 3-column row — no header needed */
          .ls-max-panel { flex-shrink: 0; height: auto; min-height: 0; padding: 8px 10px 0; overflow: hidden; position: static; width: 100%; box-sizing: border-box; flex-direction: column; gap: 0; align-items: stretch; justify-content: flex-start; border-right: none; border-bottom: 1px solid #1a1a1a; }
          .ls-max-panel::before { display: none; }
          .ls-max-panel-top { display: flex; flex-direction: row; align-items: center; width: 100%; gap: 0; justify-content: space-between; }

          /* Left col: Logo — flex:1 to mirror right col */
          .ls-panel-logo-col { display: flex; align-items: center; justify-content: center; flex: 1; }
          .ls-panel-logo { width: 80%; max-width: 72px; height: auto; }

          /* Center: Robot — fixed 42%, fully visible */
          .ls-max-box { flex: 0 0 42%; height: 165px; padding: 0; border: 1px solid #2a2a2a; }
          .ls-max-box::before, .ls-max-box::after { display: none; }
          .ls-corner-tr, .ls-corner-bl { display: none; }
          .ls-max-box-hd { display: none; }
          .ls-max-box-ft { display: none; }
          .ls-max-screen { width: 100%; height: 100%; }

          /* Right col: Info — flex:1 to mirror left col */
          .ls-panel-info-col { display: flex; flex-direction: column; justify-content: center; flex: 1; gap: 5px; padding-left: 6px; }
          .ls-panel-info-col .ls-max-ft-title { font-size: 7px; letter-spacing: 1.5px; color: #EAB000; font-family: 'Space Mono', monospace; text-transform: uppercase; line-height: 1.5; }
          .ls-panel-info-col .ls-max-ft-loc { font-size: 7px; color: #888; font-family: 'Space Mono', monospace; line-height: 1.5; }

          /* Hide unused desktop panel elements */
          .ls-max-caption { display: none; }
          .ls-trust-grid { display: none; }
          .ls-direct-line { display: none; }

          /* MAX · ACTIVE row below the 3 columns */
          .ls-max-status-bar { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 12px; padding: 5px 0 6px; }
          .ls-max-status-bar .ls-max-label { font-size: 9px; letter-spacing: 3px; color: #EAB000; font-family: 'Space Mono', monospace; }
          .ls-max-status-bar .ls-max-status { font-size: 8px; color: #aaa; font-family: 'Space Mono', monospace; display: flex; align-items: center; gap: 5px; letter-spacing: 1.5px; }
          .ls-max-status-bar .ls-status-dot { width: 6px; height: 6px; border-radius: 50%; background: #00c853; display: inline-block; }

          .ls-reset-btn { display: none; }
          .ls-cwin-hd { padding: 8px 12px; gap: 10px; }
          .ls-cwin-avatar { width: 26px; height: 26px; font-size: 9px; }
          .ls-corner-readout { display: none; }
          .ls-footer { display: none; }
        }

        /* When keyboard opens — hide Max panel to give chat full space */
        @media (max-width: 600px) and (max-height: 500px) {
          .ls-max-panel { display: none; }
        }

        /* Large desktop: expand max panel further */
        @media (min-width: 1120px) {
          .ls-max-panel { width: 600px; }
          .ls-max-box { width: 540px; }
          .ls-trust-grid { width: 540px; }
          .ls-direct-line { width: 540px; }
          .ls-max-panel::before { width: 680px; height: 680px; }
          .ls-chat-col { padding-right: 40px; }
        }
      `}</style>

      {/* ─── BODY: 2-column on desktop, stacked on mobile ─── */}
      <div className="ls-body">

        {/* ─── LEFT: MAX PANEL ─── */}
        <aside className="ls-max-panel">
          <div className="ls-max-panel-top">

            {/* Left column: Liftstein logo (replaces MAX/ACTIVE on mobile) */}
            <div className="ls-panel-logo-col">
              <img src="/logo.png" alt="Liftstein" className="ls-panel-logo" />
            </div>

            <div className="ls-max-box">
              {/* Extra corner brackets */}
              <div className="ls-corner-tr" />
              <div className="ls-corner-bl" />

              {/* Desktop only: Top bar with MAX/ACTIVE */}
              <div className="ls-max-box-hd">
                <span className="ls-max-label">MAX</span>
                <span className="ls-max-status">
                  <span className="ls-status-dot" />
                  ACTIVE
                </span>
              </div>

              {/* Video screen — MaxPlayer mounts here */}
              <div className="ls-max-screen">
                <MaxPlayer />
                <span className="ls-screen-corner ls-screen-corner-tl" />
                <span className="ls-screen-corner ls-screen-corner-tr" />
                <span className="ls-screen-corner ls-screen-corner-bl" />
                <span className="ls-screen-corner ls-screen-corner-br" />
              </div>

              {/* Desktop only: Bottom info bar */}
              <div className="ls-max-box-ft">
                <span className="ls-max-ft-title">Equipment Specialist</span>
                <span className="ls-max-ft-loc">Dubai · UAE</span>
              </div>

            </div>

            {/* Right column: Equipment info (mobile only) */}
            <div className="ls-panel-info-col">
              <span className="ls-max-ft-title">EQUIPMENT<br/>SPECIALIST</span>
              <span className="ls-max-ft-loc">Dubai · UAE</span>
            </div>

            <div className="ls-max-caption">AI · Liftstein</div>

            {/* Industrial telemetry trust grid */}
            <div className="ls-trust-grid">
              <div className="ls-trust-item">
                <span className="ls-trust-val">15+</span>
                <span className="ls-trust-key">YEARS</span>
              </div>
              <div className="ls-trust-item">
                <span className="ls-trust-val">UAE</span>
                <span className="ls-trust-key">GCC</span>
              </div>
              <div className="ls-trust-item">
                <span className="ls-trust-val">DXB</span>
                <span className="ls-trust-key">DUBAI</span>
              </div>
            </div>
          </div>

          {/* Mobile only: MAX · ACTIVE status below the 3-column row */}
          <div className="ls-max-status-bar">
            <span className="ls-max-label">MAX</span>
            <span className="ls-max-status">
              <span className="ls-status-dot" />
              ACTIVE
            </span>
          </div>

          {/* Direct-line contact */}
          <a href="https://wa.me/971557284900" target="_blank" rel="noreferrer" className="ls-direct-line">
            <div className="ls-direct-line-hd">// Direct Line</div>
            <div className="ls-direct-line-link">
              <div className="ls-direct-line-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.534 5.857L.054 23.178a.75.75 0 00.918.919l5.321-1.48A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.892 0-3.665-.523-5.183-1.433l-.371-.22-3.857 1.072 1.073-3.857-.22-.371A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              </div>
              <div className="ls-direct-line-text">
                <span className="ls-direct-line-label">WhatsApp</span>
                <span className="ls-direct-line-num">055 728 4900</span>
              </div>
            </div>
          </a>

        </aside>

        {/* ─── RIGHT: CHAT COLUMN ─── */}
        <main className="ls-chat-col">

          {/* Hero title block */}
          <div className="ls-hero">
            <span className="ls-hero-badge">Max — Intelligent Sourcing Assistant</span>
            <h1 className="ls-hero-h1">
              Find Your <span style={{ color: Y }}>Equipment</span>
              <br />In Less Than a Minute
            </h1>
            <p className="ls-hero-p">Not sure what you need? Max asks the right questions and matches you to the perfect equipment.</p>
          </div>

          {/* Chat window */}
          <div className="ls-chat-win">

            {/* Chat window header */}
            <div className="ls-cwin-hd">
              <div className="ls-cwin-avatar">M</div>
              <div style={{ flex: 1 }}>
                <div className="ls-cwin-name">Max — Equipment Specialist</div>
                <div className="ls-cwin-online">
                  <span className="ls-online-dot" />
                  Liftstein Middle East
                </div>
              </div>
              <button onClick={toggleLang} className="ls-lang-btn">
                {lang === "en" ? "عربي" : lang === "ar" ? "DE" : "EN"}
              </button>
            </div>

            {/* ── MESSAGES — all inline styles preserved exactly ── */}
            <div style={{ flex:1, overflowY:"auto", padding:"20px 18px", display:"flex", flexDirection:"column", gap:12, backgroundImage:"radial-gradient(circle, rgba(255,255,255,0.022) 1px, transparent 1px)", backgroundSize:"22px 22px" }}>
              {messages.map(function(m, i) {
                return (
                  <div key={i} className="fade">
                    {m.role === "ai" ? (
                      <div style={{ display:"flex", gap:10 }}>
                        <div style={{ width:28, height:28, borderRadius:2, background:"#EAB000", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#060606", flexShrink:0, marginTop:2, fontFamily:"'Space Mono',monospace", letterSpacing:"0.5px" }}>M</div>
                        <div style={{ maxWidth:"82%", padding:"12px 16px", borderRadius:2, borderLeft:"3px solid #EAB000", background:"#111111", fontSize:14, lineHeight:1.6, color:"#D8D6CF" }} dangerouslySetInnerHTML={{ __html: formatText(m.text) }} />
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:10, flexDirection:"row-reverse" }}>
                        <div style={{ width:28, height:28, borderRadius:2, background:"#161616", border:"1px solid #242424", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, flexShrink:0, marginTop:2 }}>👤</div>
                        <div style={{ maxWidth:"82%", padding:"12px 16px", borderRadius:2, background:Y, color:BK, fontSize:14, lineHeight:1.6, fontWeight:600 }}>{m.text}</div>
                      </div>
                    )}
                  </div>
                );
              })}

              {loading && (
                <div style={{ display:"flex", gap:10 }}>
                  <div style={{ width:28, height:28, borderRadius:2, background:"#EAB000", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#060606", flexShrink:0, fontFamily:"'Space Mono',monospace", letterSpacing:"0.5px" }}>M</div>
                  <div style={{ background:"#111", borderLeft:"3px solid #EAB000", borderRadius:2, padding:"12px 18px", display:"flex", gap:5 }}>
                    {[0,200,400].map(function(d,i){ return <span key={i} style={{ width:5, height:5, background:"#EAB000", borderRadius:1, display:"inline-block", opacity:0.6, animation:"bounce 1.2s "+d+"ms infinite" }}/>; })}
                  </div>
                </div>
              )}

              {!loading && !showLead && !itemsShown && quickReplies.length > 0 && (
                <div className="fade" style={{ display:"flex", flexWrap:"wrap", gap:8, paddingLeft:38 }}>
                  {quickReplies.map(function(q) {
                    return <button key={q} onClick={function(){ send(q); }} className="ls-qr-btn" style={{ background:"#0e0e0e", border:"1px solid #2c2c2c", color:"#9c9990", padding:"7px 14px", borderRadius:2, fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace", letterSpacing:"0.5px", transition:"border-color 0.15s,color 0.15s,background 0.15s" }}>{q}</button>;
                  })}
                </div>
              )}

              {showLead && !leadDone && (
                <div className="fade" style={{ background:"#0e0e0e", border:"1px solid #1c1c1c", borderLeft:"3px solid rgba(234,176,0,0.5)", borderRadius:0, padding:20, marginLeft:38 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#EAB000", marginBottom:14, fontFamily:"'Space Mono',monospace", letterSpacing:"2px", textTransform:"uppercase" }}>
                    {inquireItem
                      ? (lang === "ar" ? "// إرسال استفسارك عن هذا المنتج" : lang === "de" ? "// Anfrage zu diesem Produkt senden" : "// Send Your Inquiry For This Item")
                      : (lang === "ar" ? "// احصل على الأسعار والتوفر" : lang === "de" ? "// Preise & Verfügbarkeit" : "// Get Pricing & Availability")}
                  </div>
                  {inquireItem && (
                    // Shows which item this form is currently scoped to --
                    // matters once 2+ items are on screen: the customer can
                    // click Inquire on one, then change their mind and click
                    // Inquire on the other before submitting (already-typed
                    // name/phone/email are kept, only the target changes) --
                    // this line, plus the checkmarked button in the message
                    // above, makes it clear which item is currently selected.
                    <div style={{ fontSize:12, color:MT, marginBottom:10, wordBreak:"break-word" }} dir={lang === "ar" ? "rtl" : "ltr"}>
                      {lang === "ar" ? "بخصوص: " : lang === "de" ? "Bezüglich: " : "Regarding: "}
                      <span style={{ color:"#D8D6CF" }}>{inquireItem.label}</span>
                    </div>
                  )}
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    <input style={inputStyle} placeholder={lang === "ar" ? "الاسم الكامل" : lang === "de" ? "Vollständiger Name" : "Full Name"} value={leadName} onChange={function(e){ setLeadName(e.target.value); }} dir={lang === "ar" ? "rtl" : "ltr"} />
                    <input style={inputStyle} placeholder={lang === "ar" ? "رقم الواتساب / الهاتف" : lang === "de" ? "WhatsApp / Telefonnummer" : "WhatsApp / Phone Number"} value={leadPhone} onChange={function(e){ setLeadPhone(e.target.value); }} dir={lang === "ar" ? "rtl" : "ltr"} />
                    <input style={inputStyle} placeholder={lang === "ar" ? "البريد الإلكتروني" : lang === "de" ? "E-Mail-Adresse" : "Email Address"} type="email" value={leadEmail} onChange={function(e){ setLeadEmail(e.target.value); }} dir={lang === "ar" ? "rtl" : "ltr"} />
                    {!inquireItem && (
                      <textarea style={{ ...inputStyle, resize:"vertical", minHeight:80, lineHeight:1.5 }} placeholder={lang === "ar" ? "متطلباتك (اشرح باختصار ما تحتاجه)" : lang === "de" ? "Ihre Anforderungen (kurz beschreiben)" : "Your Requirements (briefly describe what you need)"} value={leadReqs} onChange={function(e){ setLeadReqs(e.target.value); }} dir={lang === "ar" ? "rtl" : "ltr"} />
                    )}
                    <button onClick={submitLead} disabled={leadSubmitting} style={{ background:"#EAB000", color:"#060606", border:"none", padding:"11px 20px", borderRadius:0, fontSize:12, fontWeight:700, cursor:leadSubmitting ? "default" : "pointer", opacity:leadSubmitting ? 0.6 : 1, width:"100%", fontFamily:"'Space Mono',monospace", letterSpacing:"1.5px", textTransform:"uppercase" }}>
                      {leadSubmitting
                        ? (lang === "ar" ? "جارٍ الإرسال..." : lang === "de" ? "Wird gesendet..." : "Sending...")
                        : inquireItem
                        ? (lang === "ar" ? "إرسال الاستفسار ←" : lang === "de" ? "Anfrage senden →" : "Send Inquiry →")
                        : (lang === "ar" ? "إرسال المتطلبات ← ليفتشتاين" : lang === "de" ? "Anforderungen senden →" : "Send Requirements →")}
                    </button>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* ── INPUT AREA — all inline styles preserved exactly ── */}
            <div style={{ padding:"12px 16px", borderTop:"1px solid #171717", background:"#080808" }}>
              {(showLead || leadDone || itemsShown) ? (
                // Once Max shows one or more items, OR the lead form, the
                // conversation is over — the customer either clicks Inquire
                // on an item / completes the form, or uses one of these two
                // exits. The free-text input never comes back in this
                // session; only "Start New Chat" resets it.
                <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"center", padding:"6px 0" }}>
                  <div style={{ fontSize:13, color:MT, marginBottom:2, textAlign:"center" }}>
                    {leadDone
                      ? (lang === "ar" ? "سيتواصل معك فريق ليفتشتاين قريباً على واتساب." : lang === "de" ? "Das Liftstein-Team wird sich bald per WhatsApp bei Ihnen melden." : "The Liftstein team will contact you shortly on WhatsApp.")
                      : showLead
                      ? (lang === "ar" ? "يرجى تعبئة النموذج أعلاه، أو التواصل معنا مباشرة:" : lang === "de" ? "Bitte füllen Sie das Formular oben aus, oder kontaktieren Sie uns direkt:" : "Please fill in the form above, or reach us directly:")
                      : (lang === "ar" ? "اضغط \"استفسار\" على أي منتج أعلاه، أو تواصل معنا مباشرة:" : lang === "de" ? "Klicken Sie oben bei einem Produkt auf \"Anfragen\", oder kontaktieren Sie uns direkt:" : "Click \"Inquire\" on any item above, or reach us directly:")}
                  </div>
                  <div style={{ display:"flex", gap:10, width:"100%" }}>
                    <a href="https://wa.me/971557284900" target="_blank" rel="noreferrer" style={{ flex:1, background:"#25D366", color:"#fff", border:"none", padding:"11px 0", borderRadius:0, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", textAlign:"center", textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.534 5.857L.054 23.178a.75.75 0 00.918.919l5.321-1.48A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.892 0-3.665-.523-5.183-1.433l-.371-.22-3.857 1.072 1.073-3.857-.22-.371A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                      {lang === "ar" ? "تواصل عبر واتساب" : lang === "de" ? "Per WhatsApp chatten" : "Chat on WhatsApp"}
                    </a>
                    <button onClick={reset} style={{ flex:1, background:"#141414", color:"#D8D6CF", border:"1px solid #252525", padding:"11px 0", borderRadius:0, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Space Mono',monospace", letterSpacing:"1px" }}>
                      {lang === "ar" ? "محادثة جديدة" : lang === "de" ? "Neuer Chat" : "Start New Chat"}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
                  <textarea
                    value={input}
                    onChange={function(e){ setInput(e.target.value); }}
                    onKeyDown={function(e){ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={lang === "ar" ? "اكتب رسالتك..." : lang === "de" ? "Nachricht eingeben..." : "Type your message..."}
                    rows={1}
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    style={{ flex:1, background:"#141414", border:"1px solid #242424", borderBottom:"2px solid rgba(234,176,0,0.35)", borderRadius:2, padding:"11px 14px", color:"#D8D6CF", fontSize:16, fontFamily:"inherit", resize:"none", outline:"none", lineHeight:1.5, textAlign: lang === "ar" ? "right" : "left" }}
                  />
                  <button onClick={function(){ send(); }} disabled={loading || !input.trim()} style={{ width:44, height:44, background: (loading || !input.trim()) ? "#1a1a1a" : "#EAB000", border:"none", borderRadius:0, cursor:(loading || !input.trim()) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background 0.15s" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={(loading || !input.trim()) ? "#333" : "#060606"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>

      {/* ─── FOOTER ─── */}
      <footer className="ls-footer">
        Powered by AI · <a href="https://liftstein.com" target="_blank" rel="noreferrer">liftstein.com</a> · Dubai, UAE
      </footer>

      {/* Fixed corner telemetry — finishes the blueprint feel on wide viewports */}
      <span className="ls-corner-readout right">UAE · DXB</span>
    </div>
  );
}
